import React, { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import Cross from "@ecologi/assets/icons/cross.svg";
import { excludePropsFromForwarding } from "../utils";
import { usePreventScroll } from "@react-aria/overlays";

const Overlay = styled(Dialog.Overlay)`
  background-color: black;
  position: fixed;
  inset: 0;
  opacity: 0.25;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 10000010;
`;

const CloseButton = styled.button`
  all: unset;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  z-index: 1000000;
  height: 20px;
  transition: opacity 0.3s ease;
  border-radius: 6px;
  padding: 8px;
  position: absolute;
  top: 24px;
  right: 32px;

  &:hover {
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    background: ${({ theme }) => theme.colors.mist};
  }

  @media ${({ theme }) => theme.bp.tabletDown} {
    right: 16px;
  }
`;

const ContentContainer = styled("div", excludePropsFromForwarding("inline"))<{
  inline: boolean;
  title?: string;
  scrollable: boolean;
}>`
  padding: ${({ inline }) => (inline ? "0" : "0px 40px 24px 40px")};
  overflow: ${({ scrollable }) => (scrollable ? "auto" : "hidden")};

  @media ${({ theme }) => theme.bp.tabletDown} {
    padding: ${({ inline }) => (inline ? "0" : "0px 24px 18px 24px")};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  margin: 24px 40px;
  justify-content: space-between;
  max-width: 550px;
  align-items: center;

  @media ${({ theme }) => theme.bp.tabletDown} {
    margin: 64px 24px 24px 24px;
  }
`;

const ModalHeaderTitle = styled(Typography)`
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.charcoal};
  font-size: 2rem;
  line-height: 2.8rem;
  align-items: center;
`;

const StyledCross = styled(Cross)`
  margin-top: 3px;
`;

const DialogContent = styled(Dialog.Content)<{
  inline: boolean;
}>`
  background-color: white;
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 24px;
  width: ${({ inline }) => (inline ? "inherit" : "704px")};
  max-height: ${({ inline }) => (inline ? "inherit" : "85%")};
  animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 10000020;

  @media ${({ theme }) => theme.bp.tabletDown} {
    border-radius: 0;
    width: ${({ inline }) => (inline ? "inherit" : "100%")};
    height: ${({ inline }) => (inline ? "inherit" : "100% !important")};
    max-height: ${({ inline }) => (inline ? "inherit" : "100% !important")};
  }
`;

export const DialogModal = ({
  title,
  children,
  className,
  isOpen = false,
  onOpen,
  onClose,
  inline = false,
  scrollable = false,
  dataTestId,
}: {
  title?: string;
  children?: ReactNode;
  className?: string;
  isOpen?: boolean;
  inline?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  scrollable?: boolean;
  dataTestId?: string;
}) => {
  usePreventScroll({ isDisabled: !(isOpen && !("ontouchstart" in window)) });

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && onOpen) {
      onOpen();
    }

    if (onClose && !isOpen) {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Overlay />
        <DialogContent
          inline={inline}
          className={className}
          data-testid={dataTestId}
        >
          <Dialog.Close asChild>
            <CloseButton data-testid={`${dataTestId}-close`}>
              Close
              <StyledCross />
            </CloseButton>
          </Dialog.Close>

          {!inline && (
            <TitleContainer>
              {title && (
                <Dialog.Title>
                  <ModalHeaderTitle
                    use="subtitle2"
                    data-testid={`${dataTestId}-title`}
                  >
                    {title}
                  </ModalHeaderTitle>
                </Dialog.Title>
              )}
            </TitleContainer>
          )}

          <ContentContainer
            inline={inline}
            title={title}
            scrollable={scrollable}
          >
            {children}
          </ContentContainer>
        </DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DialogModal;
